import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiClientError, apiClient } from './api';
import { AppShell, type AppPage } from './components/AppShell';
import { AuthScreen } from './components/AuthScreen';
import { EmptyState, ErrorNotice, Loading } from './components/Common';
import { SyncPanel } from './components/SyncPanel';
import { useAutomaticSync } from './hooks';
import { prepareOfflineDataForUser } from './offline';
import { AdminPage } from './pages/AdminPage';
import { DashboardPage } from './pages/DashboardPage';
import { PrintChecklistPage } from './pages/PrintChecklistPage';
import { RunsPage } from './pages/RunsPage';
import { WorkPage } from './pages/WorkPage';

function pageFromLocation(): AppPage {
  if (location.pathname.startsWith('/print')) return 'print';
  if (location.pathname.startsWith('/runs')) return 'runs';
  if (location.pathname.startsWith('/dashboard')) return 'dashboard';
  if (location.pathname.startsWith('/admin')) return 'admin';
  return 'work';
}

function AuthenticatedApp({
  me,
}: {
  me: NonNullable<Awaited<ReturnType<typeof apiClient.me>>['data']>;
}) {
  const queryClient = useQueryClient();
  const requestedPrintCampaignId =
    pageFromLocation() === 'print' ? new URLSearchParams(location.search).get('campaignId') : null;
  const [offlineState, setOfflineState] = useState<{
    ready: boolean;
    error: unknown;
  }>({ ready: false, error: null });
  const [campaignId, setCampaignId] = useState(
    requestedPrintCampaignId ?? me.memberships[0]?.campaignId ?? '',
  );
  const [page, setPage] = useState<AppPage>(pageFromLocation);
  const [waveId, setWaveId] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(
    me.activeRuns.find((run) => run.status === 'active')?.id ?? null,
  );
  const [syncOpen, setSyncOpen] = useState(false);
  const campaigns = useQuery({
    queryKey: ['campaigns'],
    queryFn: apiClient.campaigns,
    enabled: offlineState.ready,
  });
  useEffect(() => {
    let cancelled = false;
    setOfflineState({ ready: false, error: null });
    void prepareOfflineDataForUser(me.user.id).then(
      (result) => {
        if (!cancelled && result.clearedPreviousOwnerData) {
          queryClient.removeQueries({
            predicate: (query) => query.queryKey[0] !== 'me',
          });
        }
        if (!cancelled) setOfflineState({ ready: true, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setOfflineState({ ready: false, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [me.user.id, queryClient]);
  useAutomaticSync(me.user.id, offlineState.ready);
  useEffect(() => {
    const listener = () => setPage(pageFromLocation());
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, []);
  const navigate = (next: AppPage) => {
    const path =
      next === 'work'
        ? '/'
        : next === 'dashboard'
          ? '/dashboard'
          : next === 'admin'
            ? '/admin'
            : next === 'runs'
              ? '/runs'
              : location.pathname;
    history.pushState({}, '', path);
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (offlineState.error) return <ErrorNotice error={offlineState.error} />;
  if (!offlineState.ready) return <Loading label="Preparing offline work…" />;
  if (campaigns.isLoading) return <Loading label="Loading campaign…" />;
  if (campaigns.error) return <ErrorNotice error={campaigns.error} />;
  const availableCampaigns = campaigns.data?.data ?? [];
  const requestedPrintCampaignUnavailable =
    page === 'print' &&
    requestedPrintCampaignId !== null &&
    !availableCampaigns.some((item) => item.id === requestedPrintCampaignId);
  if (requestedPrintCampaignUnavailable) {
    return (
      <ErrorNotice
        error={new Error('This print checklist belongs to a campaign you cannot access.')}
      />
    );
  }
  const campaign =
    availableCampaigns.find((item) => item.id === campaignId) ?? availableCampaigns[0] ?? null;
  if (!campaign) {
    return (
      <EmptyState title="No campaign access">
        Ask an administrator to add your account to a campaign.
      </EmptyState>
    );
  }
  const role =
    me.memberships.find((membership) => membership.campaignId === campaign.id)?.role ??
    campaign.role;
  const effectivePage =
    role === 'viewer' && ['work', 'runs', 'admin'].includes(page) ? 'dashboard' : page;
  if (effectivePage === 'print') return <PrintChecklistPage campaign={campaign} />;
  let content;
  if (effectivePage === 'work') {
    content = (
      <WorkPage
        key={campaign.id}
        campaign={campaign}
        me={me}
        waveId={waveId}
        runId={runId}
        onWave={setWaveId}
        onRun={setRunId}
      />
    );
  } else if (effectivePage === 'runs') {
    content = <RunsPage key={campaign.id} campaign={campaign} me={me} role={role} />;
  } else if (effectivePage === 'admin') {
    content = <AdminPage key={campaign.id} campaign={campaign} role={role} />;
  } else {
    content = <DashboardPage key={campaign.id} campaign={campaign} />;
  }
  return (
    <AppShell
      me={me}
      campaign={campaign}
      campaigns={availableCampaigns}
      page={effectivePage}
      role={role}
      onPage={navigate}
      onCampaign={(nextCampaignId) => {
        setCampaignId(nextCampaignId);
        setWaveId(null);
        setRunId(null);
      }}
      onOpenSync={() => setSyncOpen(true)}
    >
      {content}
      {syncOpen ? <SyncPanel ownerUserId={me.user.id} onClose={() => setSyncOpen(false)} /> : null}
    </AppShell>
  );
}

export function App() {
  const me = useQuery({
    queryKey: ['me'],
    queryFn: apiClient.me,
    retry: false,
  });
  if (me.isLoading) return <Loading label="Opening flyer operations…" />;
  if (
    me.error instanceof ApiClientError &&
    ['authentication_required', 'session_expired'].includes(me.error.code)
  ) {
    return <AuthScreen />;
  }
  if (me.error) return <ErrorNotice error={me.error} />;
  if (!me.data) return <AuthScreen />;
  return <AuthenticatedApp me={me.data.data} />;
}
